import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Radio } from "lucide-react";
import { NAV, SITE } from "@/lib/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname, setOpen]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [setScrolled]);

  return (
    <>
      {/* Top emergency strip */}
      <div className="ember-stripe siren-sweep emergency-strip text-white text-xs tracking-wide relative overflow-hidden" data-testid="emergency-strip">
        <div className="container-gaza flex items-center justify-between py-2 relative z-10">
          <div className="flex items-center gap-2.5">
            <span className="signal-icon" aria-hidden>
              <Radio className="w-3.5 h-3.5 relative z-10" strokeWidth={2.4} />
              <span className="signal-ring" />
              <span className="signal-ring signal-ring-delay" />
            </span>
            <span className="emergency-dot"></span>
            <span className="uppercase tracking-[0.22em] font-bold text-[11px] emergency-text">
              <span className="emergency-text-glow">24/7 Emergency Response</span>
            </span>
            <span className="hidden sm:inline text-white/75">· Toronto & the GTA</span>
          </div>
          <a href={SITE.phoneHref} className="hidden sm:inline-flex items-center gap-2 hover:text-[hsl(var(--gaza-navy))] transition font-bold group" data-testid="emergency-phone-link">
            <Phone className="w-3.5 h-3.5 group-hover:animate-bounce" /> <span>{SITE.phone}</span>
          </a>
        </div>
        {/* Scan-line bottom border */}
        <span className="emergency-scan-line" aria-hidden />
      </div>

      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/95 backdrop-blur-md border-b border-[hsl(var(--gaza-line))] shadow-[0_8px_30px_-20px_rgba(11,27,46,0.25)]" : "bg-white border-b border-transparent"
        }`}
        data-testid="site-navbar"
      >
        <div className="container-gaza flex items-center justify-between h-[72px]">
          <Link to="/" className="group flex items-center gap-3" data-testid="brand-link">
            <img
              src={`${process.env.PUBLIC_URL || ""}/brand/backhome_lockup.png`}
              alt="BackHome Restoration Inc."
              className="h-11 md:h-14 w-auto"
              data-testid="brand-logo"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[13px] uppercase tracking-[0.18em] font-semibold link-underline transition-colors ${
                    isActive ? "text-[hsl(var(--gaza-gold))]" : "text-[hsl(var(--gaza-navy))] hover:text-[hsl(var(--gaza-gold))]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href={SITE.phoneHref} className="btn-gold" data-testid="nav-call-btn">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
          </div>

          <button
            className="lg:hidden p-2 text-[hsl(var(--gaza-navy))]"
            onClick={() => setOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-[hsl(var(--gaza-line))] bg-white" data-testid="mobile-menu">
            <div className="container-gaza py-6 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `py-3 text-base uppercase tracking-[0.18em] font-semibold border-b border-[hsl(var(--gaza-line))] ${
                      isActive ? "text-[hsl(var(--gaza-gold))]" : "text-[hsl(var(--gaza-navy))]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <a href={SITE.phoneHref} className="btn-gold mt-5" data-testid="mobile-call-btn">
                <Phone className="w-4 h-4" /> Call {SITE.phone}
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
